import React, { useState, useEffect } from 'react';
import {
  Package,
  FileText,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Clock,
  DollarSign,
  Users,
  BarChart3,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Brain,
  RefreshCw,
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import API_CONFIG from '../../config/api';
import { useToast } from '../common/Toast';

const Dashboard = () => {
  const { success, error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAssets: 0,
    activeContracts: 0,
    pendingApprovals: 0,
    totalValue: 0,
    monthlyGrowth: 8.5,
    contractGrowth: 12.3,
  });

  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingRenewals, setUpcomingRenewals] = useState([]);

  // Load dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const response = await fetch(API_CONFIG.ENDPOINTS.DASHBOARD, {
        headers: API_CONFIG.HEADERS,
      });

      if (response.ok) {
        const data = await response.json();
        setStats({
          totalAssets: data.stats?.totalAssets || 0,
          activeContracts: data.stats?.activeContracts || 0,
          pendingApprovals: data.stats?.pendingApprovals || 0,
          totalValue: data.stats?.totalValue || 0,
          monthlyGrowth: data.stats?.monthlyGrowth || 0,
          contractGrowth: data.stats?.contractGrowth || 0,
        });
        setRecentActivities(data.recentActivities || []);
        setUpcomingRenewals(data.upcomingRenewals || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Error loading dashboard data:', errorData);
        showError(errorData.message || 'Failed to load dashboard data');
        // Keep default empty state
      }
    } catch (err) {
      console.error('Error loading dashboard data:', err);
      showError(`Network error: ${err.message}`);
      // Keep default empty state
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return '#059669';
      case 'pending':
        return '#F59E0B';
      case 'warning':
        return '#EF4444';
      case 'info':
        return '#6B7280';
      default:
        return '#6B7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} />;
      case 'pending':
        return <Clock size={16} />;
      case 'warning':
        return <AlertTriangle size={16} />;
      case 'info':
        return <BarChart3 size={16} />;
      default:
        return <Clock size={16} />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Dashboard"
        description="Welcome to AssetContract Pro - Your AI-powered asset and contract management platform"
        actions={[
          {
            color: 'var(--gold-primary)',
            icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
            onClick: handleRefresh,
            variant: 'outline',
            tooltip: 'Refresh dashboard data',
            disabled: loading,
          },
        ]}
      />
      <div className="page-container">
        {loading ? (
          <div className="flex justify-center items-center min-h-[400px] flex-col gap-4">
            <div className="loading-spinner"></div>
            <p className="text-[var(--text-secondary)] font-[var(--font-family)]">
              Loading dashboard data...
            </p>
          </div>
        ) : (
          <>
            <div className="justify-center max-w-[1200px] mx-auto w-full mb-8 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
              <button className="bg-white border !border-[var(--gold-primary)] text-[var(--gold-primary)] !rounded-full h-[var(--button-height)] px-5 font-medium text-[15px] cursor-pointer transition-all duration-200 flex items-center justify-center gap-2">
                <Package size={20} className="text-[var(--gold-primary)]" />
                <span className="text-sm font-semibold">Add New Asset</span>
              </button>

              <button className="bg-white border !border-[var(--gold-primary)] text-[var(--gold-primary)] !rounded-full h-[var(--button-height)] px-5 font-medium text-[15px] cursor-pointer transition-all duration-200 flex items-center justify-center gap-2">
                <FileText size={20} className="text-[var(--gold-primary)]" />
                <span className="text-sm font-semibold">Create Contract</span>
              </button>

              <button className="bg-white border !border-[var(--gold-primary)] text-[var(--gold-primary)] !rounded-full h-[var(--button-height)] px-5 font-medium text-[15px] cursor-pointer transition-all duration-200 flex items-center justify-center gap-2">
                <Brain size={20} className="text-[var(--gold-primary)]" />
                <span className="text-sm font-semibold">AI Insights</span>
              </button>

              <button className="bg-white border !border-[var(--gold-primary)] text-[var(--gold-primary)] !rounded-full h-[var(--button-height)] px-5 font-medium text-[15px] cursor-pointer transition-all duration-200 flex items-center justify-center gap-2">
                <BarChart3 size={20} className="text-[var(--gold-primary)]" />
                <span className="text-sm font-semibold">View Reports</span>
              </button>
            </div>

            {/* KPI Cards Grid - Matching OverviewLanding spacing */}
            <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
              {/* Total Assets */}
              <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                {/* Accent line */}
                <div className="h-1 w-full bg-[var(--gold-primary)]" />

                {/* Content */}
                <div className="relative flex items-center gap-3 p-4">
                  {/* Growth – top right */}
                  <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
                    <ArrowUpRight size={14} />+{stats.monthlyGrowth}%
                  </div>

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                    <Package size={20} className="text-[var(--gold-primary)]" />
                  </div>

                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {stats.totalAssets.toLocaleString()}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      Total Assets
                    </div>
                  </div>
                </div>
              </div>

              {/* Active Contracts */}
              <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                <div className="h-1 w-full bg-[var(--gold-primary)]" />

                <div className="relative flex items-center gap-3 p-4">
                  <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
                    <ArrowUpRight size={14} />+{stats.contractGrowth}%
                  </div>

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                    <FileText size={20} className="text-[var(--gold-primary)]" />
                  </div>

                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {stats.activeContracts}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      Active Contracts
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Approvals */}
              <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                <div className="relative flex items-center gap-3 p-4">
                  <div className="absolute right-4 top-3 rounded-full bg-[#FEF3C7] px-3 py-1 text-[11px] font-semibold text-[#92400E]">
                    Action Required
                  </div>

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FEF3C7]">
                    <Clock size={20} className="text-[#92400E]" />
                  </div>

                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {stats.pendingApprovals}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      Pending Approvals
                    </div>
                  </div>
                </div>
              </div>

              {/* Total Contract Value */}
              <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                <div className="relative flex items-center gap-3 p-4">
                  <div className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold text-[#059669]">
                    <ArrowUpRight size={14} />
                    +5.2%
                  </div>

                  <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#D6EFFF]">
                    <DollarSign size={20} className="text-[#2D7DD2]" />
                  </div>

                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {formatCurrency(stats.totalValue)}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      Total Contract Value
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Main Content Grid - Matching OverviewLanding spacing */}
            <div className="max-w-[1200px] mx-auto w-full mt-8">
              <div className="grid grid-cols-[2fr_1fr] gap-6">
                {/* Recent Activities */}
                <div className="asset-landing-card rounded-2xl mb-8">
                  <div className="p-6">
                    <div className="d-flex align-items-center mb-3">
                      <div className="w-10 h-10 rounded-[10px] bg-[#FFF9E6] flex items-center justify-center mr-4">
                        <div className="text-[var(--gold-primary)] asset-card-icon">
                          <BarChart3 size={20} />
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4
                          className="text-lg font-semibold text-[var(--text-primary)] m-0 "
                          style={{ fontFamily: 'var(--font-family)' }}
                        >
                          Recent Activities
                        </h4>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {recentActivities.map((activity) => (
                        <div
                          key={activity.id}
                          className="flex items-start gap-3 p-4 bg-[#F6FAFF] rounded-[var(--input-radius)] border border-[rgba(0,0,0,0.06)] transition-all duration-200 cursor-pointer hover:bg-[#EDF3F9] hover:border-[rgba(45,125,210,0.2)]"
                        >
                          <div
                            style={{ color: getStatusColor(activity.status) }}
                            className="mt-0.5 flex-shrink-0"
                          >
                            {getStatusIcon(activity.status)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div
                              className="text-sm font-semibold text-[var(--text-primary)] mb-1.5"
                              style={{ fontFamily: 'var(--font-family)' }}
                            >
                              {activity.title}
                            </div>
                            <div className="text-[13px] text-[var(--text-secondary)] mb-1.5 font-[var(--font-family)]">
                              {activity.description}
                            </div>
                            <div className="text-xs text-[var(--text-secondary)] font-[var(--font-family)]">
                              {activity.time}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Upcoming Renewals */}
                <div className="asset-landing-card rounded-2xl mb-8">
                  <div className="p-6">
                    <div className="d-flex align-items-center mb-3">
                      <div className="w-10 h-10 rounded-[10px] bg-[#FFF9E6] flex items-center justify-center mr-4">
                        <div className="text-[var(--gold-primary)] asset-card-icon">
                          <Calendar size={20} />
                        </div>
                      </div>
                      <div className="flex-grow-1">
                        <h4 className="text-lg text-[var(--text-primary)] m-0 font-[var(--font-family)]">
                          Upcoming Renewals
                        </h4>
                      </div>
                    </div>
                    <div className="flex flex-col gap-3">
                      {upcomingRenewals.map((renewal) => (
                        <div
                          key={renewal.id}
                          className="p-4 bg-[#FEF2F2] rounded-[var(--input-radius)] border border-[#FECACA] transition-all duration-200 cursor-pointer hover:bg-[#FEE2E2] hover:border-[#FCA5A5]"
                        >
                          <div className="text-sm text-[var(--text-primary)] mb-1.5 font-[var(--font-family)]">
                            {renewal.title}
                          </div>
                          <div className="text-[13px] text-[var(--text-secondary)] mb-3 font-[var(--font-family)]">
                            {renewal.counterparty}
                          </div>
                          <div className="flex justify-between items-center">
                            <div
                              className="text-xs text-[#DC2626] font-semibold"
                              style={{ fontFamily: 'var(--font-family)' }}
                            >
                              {renewal.daysLeft} days left
                            </div>
                            <div
                              className="text-[13px] text-[var(--text-primary)] font-semibold"
                              style={{ fontFamily: 'var(--font-family)' }}
                            >
                              {formatCurrency(renewal.value)}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Dashboard;
